import { format, isSameDay } from "date-fns";
import { cn } from "@/lib/utils";
import { dayKeyFromDate } from "@/lib/v2/pollAggregates";

export interface WeekDayPickerDay {
  date: Date;
  label?: string;
}

interface WeekDayPickerProps {
  days: WeekDayPickerDay[];
  selected: Date[];
  onChange: (days: Date[]) => void;
  max?: number;
  showAvailability?: boolean;
  availability?: Record<string, number>;
  disabled?: boolean;
  /** Dates currently syncing — only those cells are non-interactive. */
  pendingDates?: string[];
  hint?: string;
  compact?: boolean;
  /** Horizontal strip — slides one day at a time inside a fixed window. */
  slidingWindow?: {
    offset: number;
    windowSize?: number;
    animate?: boolean;
  };
}

const WEEKDAY_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function formatWeekdayShort(date: Date) {
  return WEEKDAY_SHORT[date.getDay()];
}

export function WeekDayPicker({
  days,
  selected,
  onChange,
  max,
  showAvailability = false,
  availability = {},
  disabled = false,
  pendingDates = [],
  hint,
  compact = false,
  slidingWindow,
}: WeekDayPickerProps) {
  const pending = new Set(pendingDates);

  const toggle = (date: Date) => {
    const key = dayKeyFromDate(date);
    if (disabled || pending.has(key)) return;
    const isSelected = selected.some((d) => isSameDay(d, date));
    if (isSelected) {
      onChange(selected.filter((d) => !isSameDay(d, date)));
      return;
    }
    if (max !== undefined && selected.length >= max) return;
    onChange([...selected, date].sort((a, b) => a.getTime() - b.getTime()));
  };

  const renderDayCell = (date: Date) => {
    const isSelected = selected.some((d) => isSameDay(d, date));
    const key = dayKeyFromDate(date);
    const count = availability[key] ?? 0;
    const atMax = max !== undefined && selected.length >= max && !isSelected;
    const isPending = pending.has(key);

    return (
      <button
        key={key}
        type="button"
        onClick={() => toggle(date)}
        disabled={disabled || atMax || isPending}
        aria-pressed={isSelected}
        aria-busy={isPending}
        className={cn(
          "relative flex w-full min-w-0 flex-col items-center justify-center rounded-lg border-2 px-0.5 transition-transform active:scale-[0.97]",
          compact ? "min-h-[44px] py-1" : "py-2",
          isSelected
            ? "border-soccer-primary bg-soccer-primary text-white shadow-sm"
            : atMax
              ? "cursor-not-allowed border-border bg-card opacity-40"
              : isPending
                ? "cursor-wait border-border bg-card opacity-70"
                : "border-border bg-card hover:border-soccer-primary/40 hover:bg-muted/40"
        )}
      >
        <span
          className={cn(
            "font-semibold leading-none",
            compact ? "text-[10px]" : "text-xs",
            isSelected ? "text-white/90" : "text-muted-foreground"
          )}
        >
          {formatWeekdayShort(date)}
        </span>
        <span
          className={cn(
            "font-bold leading-none tabular-nums",
            compact ? "mt-0 text-sm" : "mt-0.5 text-lg"
          )}
        >
          {format(date, "d")}
        </span>
        {showAvailability && !isSelected && !isPending && count > 0 ? (
          <span
            className={cn(
              "font-bold tabular-nums",
              compact ? "mt-0 text-[10px]" : "mt-1 text-xs",
              count >= 6 ? "text-soccer-primary" : "text-muted-foreground"
            )}
          >
            {count}
          </span>
        ) : null}
      </button>
    );
  };

  if (slidingWindow) {
    const windowSize = slidingWindow.windowSize ?? 7;
    const totalDays = days.length;
    const offset = slidingWindow.offset;
    const shouldAnimate = slidingWindow.animate !== false;

    return (
      <div className="space-y-2">
        <div className="overflow-hidden">
          <div
            className={cn(
              "grid gap-1.5",
              shouldAnimate &&
                "transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
            )}
            style={{
              gridTemplateColumns: `repeat(${totalDays}, minmax(0, 1fr))`,
              width: `${(totalDays / windowSize) * 100}%`,
              transform: `translateX(calc(-100% * ${offset} / ${totalDays}))`,
            }}
          >
            {days.map(({ date }) => renderDayCell(date))}
          </div>
        </div>
        {hint ? <p className="text-center text-sm text-muted-foreground">{hint}</p> : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-7 gap-1.5">
        {days.map(({ date }) => renderDayCell(date))}
      </div>
      {hint ? (
        <p className="text-center text-sm text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
